// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Renders an agent-produced, validated chart spec with Recharts (T-302). The
// spec is re-validated here; if it's malformed we fall back to a simple data
// table rather than rendering garbage.

import { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { ChartSpec, parseChartSpec, CHART_COLORS } from "@/lib/chartSpec";

interface GenerativeChartProps {
  spec: unknown;
}

function colorFor(index: number, explicit?: string | null): string {
  return explicit || CHART_COLORS[index % CHART_COLORS.length];
}

function DataTableFallback({ spec }: { spec: ChartSpec }) {
  const cols = [spec.x.key, ...spec.series.map((s) => s.key)];
  return (
    <div className="overflow-auto max-h-72 rounded border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50">
          <tr>
            {cols.map((c) => (
              <th key={c} className="px-2 py-1 text-left font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {spec.data.slice(0, 50).map((row, i) => (
            <tr key={i} className="border-t border-border">
              {cols.map((c) => (
                <td key={c} className="px-2 py-1">{String(row[c] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GenerativeChart({ spec }: GenerativeChartProps) {
  const parsed = useMemo(() => parseChartSpec(spec), [spec]);

  if (!parsed) {
    return (
      <div className="text-xs text-muted-foreground italic p-2">
        Chart could not be rendered (invalid spec).
      </div>
    );
  }

  const { type, title, x, series, stacked, data } = parsed;

  const body = (() => {
    switch (type) {
      case "line":
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={x.key} fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip />
            <Legend />
            {series.map((s, i) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key} stroke={colorFor(i, s.color)} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        );
      case "area":
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={x.key} fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip />
            <Legend />
            {series.map((s, i) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key} stackId={stacked ? "1" : undefined} stroke={colorFor(i, s.color)} fill={colorFor(i, s.color)} fillOpacity={0.25} />
            ))}
          </AreaChart>
        );
      case "bar":
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={x.key} fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip />
            <Legend />
            {series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.label || s.key} stackId={stacked ? "1" : undefined} fill={colorFor(i, s.color)} />
            ))}
          </BarChart>
        );
      case "pie": {
        const s = series[0];
        return (
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie data={data} dataKey={s.key} nameKey={x.key} cx="50%" cy="50%" outerRadius={90} label>
              {data.map((_, i) => (
                <Cell key={i} fill={colorFor(i)} />
              ))}
            </Pie>
          </PieChart>
        );
      }
      case "scatter":
        return (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey={x.key} fontSize={12} />
            <YAxis dataKey={series[0].key} fontSize={12} />
            <Tooltip />
            <Legend />
            {series.map((s, i) => (
              <Scatter key={s.key} name={s.label || s.key} data={data} fill={colorFor(i, s.color)} />
            ))}
          </ScatterChart>
        );
      default:
        return null;
    }
  })();

  if (!body) {
    return <DataTableFallback spec={parsed} />;
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-background/60 p-3">
      {title && <p className="text-sm font-semibold mb-2">{title}</p>}
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          {body}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default GenerativeChart;
